import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/hooks/useAuth";
import {
  validatePassword,
  validatePasswordConfirmation,
} from "@/utils/formValidation";
import { toast } from "sonner";
import { authLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const ResetPassword = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const token = query.get("token") || "";
  const { resetPasswordAsync, isResettingPassword, validateResetTokenAsync } =
    useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const { language } = useLanguage();
  const labels = useMemo(() => authLabels(language), [language]);

  // Redirect if no token
  useEffect(() => {
    if (!token) {
      toast.error(labels.error?.invalidToken);
      navigate("/auth", { replace: true });
      return;
    }

    // Validate token when component mounts
    const validateToken = async () => {
      setIsValidating(true);
      try {
        const isValid = await validateResetTokenAsync(token);
        setTokenValid(isValid);
        if (!isValid) {
          toast.error(labels.error?.tokenExpired);
        }
      } catch (error) {
        setTokenValid(false);
        toast.error(labels.error?.couldNotValidateLink);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, navigate, validateResetTokenAsync, labels]);

  const pwdValidation = useMemo(() => validatePassword(password), [password]);
  const confirmValidation = useMemo(
    () => validatePasswordConfirmation(password, confirm),
    [password, confirm]
  );

  const canSubmit =
    tokenValid === true && pwdValidation.isValid && confirmValidation.isValid;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await resetPasswordAsync({ token, password });
      toast.success(labels.success?.passwordReset);
      navigate("/auth");
    } catch (e: unknown) {
      const errorMessage = (e as { message?: string })?.message ?? "";
      // Check if error is about password being same as old password
      const isSamePasswordError =
        errorMessage.toLowerCase().includes("must be different") ||
        errorMessage.toLowerCase().includes("same as") ||
        errorMessage.toLowerCase().includes("måste skilja");

      const message = isSamePasswordError
        ? labels.error?.passwordSameAsOldFriendly
        : errorMessage || labels.error?.couldNotResetPassword;
      toast.error(message);
    }
  };

  // Show loading state while validating token
  if (isValidating || tokenValid === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 space-y-4 glass-card">
          <div className="text-lg font-semibold">
            {labels.title?.resetPassword}
          </div>
          <div className="text-sm text-muted-foreground">
            {labels.resetPassword?.checkingLink}
          </div>
        </Card>
      </div>
    );
  }

  // Show error if token is invalid
  if (tokenValid === false) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 space-y-4 glass-card">
          <div className="text-lg font-semibold">
            {labels.title?.resetPassword}
          </div>
          <div className="text-sm text-red-500">
            {labels.resetPassword?.invalidOrExpiredLink}
          </div>
          <Button className="w-full" onClick={() => navigate("/auth")}>
            {labels.buttons?.backToLogin}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6 space-y-4 glass-card">
        <div className="text-lg font-semibold">
          {labels.title?.resetPassword}
        </div>
        <div className="space-y-2">
          <PasswordInput
            id="newPassword"
            label={labels.forms?.fields?.newPassword}
            value={password}
            onChange={setPassword}
          />
          {!pwdValidation.isValid && (
            <div className="text-xs text-red-500">
              {labels.forms?.validation?.passwordRequirements}
            </div>
          )}
          <PasswordInput
            id="confirmPassword"
            label={labels.forms?.fields?.confirmNewPassword}
            value={confirm}
            onChange={setConfirm}
            validator={(v) => {
              const r = validatePasswordConfirmation(password, v);
              return {
                ...r,
                errors: r.errors.map((err) =>
                  err.includes("matchar inte") || err.includes("do not match")
                    ? (labels.forms?.validation?.passwordsDoNotMatch ?? err)
                    : err
                ),
              };
            }}
            showRequirements={false}
          />
        </div>
        <Button
          className="w-full"
          onClick={handleSubmit}
          disabled={!canSubmit || isResettingPassword}
        >
          {isResettingPassword
            ? labels.buttons?.saving
            : labels.buttons?.saveNewPassword}
        </Button>
      </Card>
    </div>
  );
};

export default ResetPassword;
